import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useAuth, apiError } from "../context/AuthContext";
import { api } from "../lib/apiClient";
import { LogIn, UserPlus, KeyRound, ArrowLeft, Copy, ExternalLink, Loader2 } from "lucide-react";
import { SaraLogo } from "./SaraLogo";

export function AuthScreen() {
  const { login, register } = useAuth();
  const [params, setParams] = useSearchParams();
  const resetToken = params.get("reset") || "";
  const [mode, setMode] = useState(resetToken ? "reset" : "login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [name, setName] = useState("");
  const [error, setError] = useState("");
  const [info, setInfo] = useState("");
  const [resetLink, setResetLink] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (resetToken) setMode("reset");
  }, [resetToken]);

  const switchMode = (m) => {
    setMode(m);
    setError("");
    setInfo("");
    setResetLink("");
    setPassword("");
    setConfirm("");
    if (m !== "reset" && resetToken) {
      params.delete("reset");
      setParams(params, { replace: true });
    }
    if (m === "forgot") {
      setInfo("Pour réinitialiser votre mot de passe, contactez votre administrateur.");
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setInfo("");
    setLoading(true);
    try {
      if (mode === "login") {
        await login(email, password);
      } else if (mode === "register") {
        const res = await register(email, password, name);
        if (res && res.pending_approval) {
          setInfo(res.message || "Compte créé. Un administrateur doit le valider avant ta première connexion.");
          setPassword("");
          setConfirm("");
          setName("");
          setMode("login");
        }
      } else if (mode === "forgot") {
        switchMode("login");
        return;
      } else if (mode === "reset") {
        if (password.length < 6) throw new Error("Le mot de passe doit faire au moins 6 caractères.");
        if (password !== confirm) throw new Error("Les deux mots de passe ne correspondent pas.");
        const { data } = await api.post("/auth/reset-password", { token: resetToken, password });
        setEmail(data.email || "");
        setInfo("Mot de passe réinitialisé. Connecte-toi avec ton nouveau mot de passe.");
        setPassword("");
        setConfirm("");
        params.delete("reset");
        setParams(params, { replace: true });
        setMode("login");
      }
    } catch (err) {
      setError(err?.response ? apiError(err) : err.message || "Erreur");
    } finally {
      setLoading(false);
    }
  };

  const title =
    mode === "forgot" ? "RÉINITIALISATION" : mode === "reset" ? "NOUVEAU MOT DE PASSE" : "AUTHENTIFICATION OPÉRATEUR";

  return (
    <div
      className="fixed inset-0 z-40 flex items-center justify-center overflow-y-auto px-6 py-10"
      data-testid="auth-modal"
    >
      <div className="w-full max-w-md animate-rise">
        <div className="mb-7 flex flex-col items-center text-center">
          <SaraLogo className="h-14 w-14 mb-4" />
          <h1 className="text-3xl font-extrabold tracking-tight text-white">
            SARA <span className="text-sara-gradient">CONNOR</span>
          </h1>
          <p className="mt-2 font-jb text-[10px] tracking-[0.35em] text-cyan-400/70">{title}</p>
        </div>

        <div className="glass-card rounded-2xl p-7 shadow-[0_0_50px_rgba(0,240,255,0.12)] relative overflow-hidden">
          <div className="pointer-events-none absolute -top-16 left-1/4 h-40 w-40 rounded-full bg-cyan-500/20 blur-3xl" />

          {mode === "login" || mode === "register" ? (
            <div className="relative flex mb-6 rounded-xl border border-cyan-500/20 p-1 bg-[#050b14]/60">
              {["login", "register"].map((m) => (
                <button
                  key={m}
                  data-testid={`auth-tab-${m}`}
                  onClick={() => switchMode(m)}
                  className={`flex-1 py-2.5 rounded-lg font-jb text-[11px] tracking-[0.18em] uppercase transition-all ${
                    mode === m
                      ? "bg-gradient-to-r from-cyan-500/90 to-blue-600/90 text-white shadow-[0_0_18px_rgba(0,240,255,0.35)]"
                      : "text-slate-400 hover:text-white"
                  }`}
                >
                  {m === "login" ? "Connexion" : "Inscription"}
                </button>
              ))}
            </div>
          ) : (
            <button
              data-testid="auth-back-to-login"
              onClick={() => switchMode("login")}
              className="mb-5 flex items-center gap-1.5 font-jb text-[10px] uppercase tracking-[0.2em] text-slate-400 hover:text-cyan-300 transition-colors"
            >
              <ArrowLeft className="h-3.5 w-3.5" /> Retour à la connexion
            </button>
          )}

          <form onSubmit={submit} className="space-y-4 relative">
            {mode === "register" && (
              <Field label="NOM DE L'OPÉRATEUR" testid="auth-name-input" value={name} onChange={setName} placeholder="Tony Stark" required />
            )}

            {mode !== "reset" && mode !== "forgot" && (
              <Field
                label="E-MAIL"
                testid="auth-email-input"
                type="email"
                value={email}
                onChange={setEmail}
                placeholder="operateur@stark.io"
                required
              />
            )}

            {mode !== "forgot" && (
              <Field
                label={mode === "reset" ? "NOUVEAU MOT DE PASSE" : "MOT DE PASSE"}
                testid="auth-password-input"
                type="password"
                value={password}
                onChange={setPassword}
                placeholder="••••••••"
                required
              />
            )}

            {mode === "reset" && (
              <Field
                label="CONFIRMATION"
                testid="auth-confirm-input"
                type="password"
                value={confirm}
                onChange={setConfirm}
                placeholder="••••••••"
                required
              />
            )}

            {error && (
              <p data-testid="auth-error" className="font-jb text-[11px] text-rose-400 tracking-wide">
                » {error}
              </p>
            )}
            {info && (
              <p data-testid="auth-info" className="font-jb text-[11px] text-emerald-400 tracking-wide">
                » {info}
              </p>
            )}

            {resetLink && <ResetLinkBox link={resetLink} />}

            <button
              data-testid="auth-submit-button"
              disabled={loading}
              type="submit"
              className="w-full py-3.5 rounded-xl flex items-center justify-center gap-2 text-sm font-semibold text-white bg-gradient-to-r from-cyan-500 via-blue-600 to-indigo-600 shadow-[0_0_20px_rgba(0,240,255,0.4)] hover:shadow-[0_0_32px_rgba(0,240,255,0.7)] transition-all duration-300 disabled:opacity-50"
            >
              {loading ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : mode === "login" ? (
                <LogIn className="h-4 w-4" />
              ) : mode === "register" ? (
                <UserPlus className="h-4 w-4" />
              ) : (
                <KeyRound className="h-4 w-4" />
              )}
              {loading
                ? "TRAITEMENT..."
                : mode === "login"
                ? "ACCÉDER AU TERMINAL"
                : mode === "register"
                ? "CRÉER L'OPÉRATEUR"
                : mode === "forgot"
                ? "J'AI COMPRIS"
                : "ENREGISTRER LE MOT DE PASSE"}
            </button>
          </form>

          {mode === "login" && (
            <button
              data-testid="auth-forgot-link"
              onClick={() => switchMode("forgot")}
              className="mt-4 w-full text-center font-jb text-[11px] tracking-[0.18em] uppercase text-slate-400 hover:text-cyan-300 transition-colors"
            >
              Mot de passe oublié ?
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function ResetLinkBox({ link }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(link);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (_) {}
  };
  return (
    <div data-testid="auth-reset-link-box" className="rounded-xl border border-cyan-500/40 bg-cyan-500/5 p-3">
      <p className="font-jb text-[9px] uppercase tracking-[0.2em] text-cyan-300 mb-2">Lien de réinitialisation</p>
      <code data-testid="auth-reset-link" className="block break-all font-mono text-[11px] text-[#eaf4ff]">
        {link}
      </code>
      <div className="mt-2.5 flex gap-2">
        <button
          type="button"
          data-testid="auth-reset-copy"
          onClick={copy}
          className="flex items-center gap-1.5 rounded-lg border border-cyan-500/20 px-2.5 py-1.5 font-jb text-[10px] uppercase tracking-widest text-slate-400 hover:text-white transition-colors"
        >
          <Copy className="h-3 w-3" /> {copied ? "Copié" : "Copier"}
        </button>
        <a
          href={link}
          data-testid="auth-reset-open"
          className="flex items-center gap-1.5 rounded-lg border border-cyan-500/40 px-2.5 py-1.5 font-jb text-[10px] uppercase tracking-widest text-cyan-300 hover:bg-cyan-500/10 transition-colors"
        >
          <ExternalLink className="h-3 w-3" /> Ouvrir
        </a>
      </div>
      <p className="mt-2 font-jb text-[9px] text-slate-500 leading-relaxed">
        Mode sans e-mail : le lien s'affiche ici. Branche Resend plus tard pour un envoi automatique.
      </p>
    </div>
  );
}

function Field({ label, testid, type = "text", value, onChange, placeholder, required }) {
  return (
    <label className="block">
      <span className="font-jb text-[10px] tracking-[0.25em] text-slate-400">{label}</span>
      <input
        data-testid={testid}
        type={type}
        value={value}
        required={required}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="mt-1.5 w-full rounded-xl bg-[#050b14]/80 border border-cyan-500/30 px-4 py-3 text-sm text-white outline-none focus:border-cyan-400 focus:ring-1 focus:ring-cyan-400 focus:shadow-[0_0_20px_rgba(0,240,255,0.25)] transition-all duration-200 placeholder:text-slate-600"
      />
    </label>
  );
}
